import Cookies from "js-cookie";
import { getAppOrigin } from "@/lib/siteHosts";

export function getAuthCookieOptions() {
  const isSecure = typeof window !== "undefined" && window.location.protocol === "https:";
  const hostname = typeof window !== "undefined" ? window.location.hostname.toLowerCase() : "";
  const options = { expires: 7, secure: isSecure, sameSite: "lax" };
  if (hostname === "fractolabs.com" || hostname.endsWith(".fractolabs.com")) {
    options.domain = ".fractolabs.com";
  }
  return options;
}

function removeCookie(name) {
  Cookies.remove(name);
  const { domain } = getAuthCookieOptions();
  if (domain) {
    Cookies.remove(name, { path: "/", domain });
  }
}

export function setAuthSessionCookies({ token, email, accountType, botType }) {
  const opts = getAuthCookieOptions();
  Cookies.set("token", token, opts);
  Cookies.set("email", email.toLowerCase(), opts);
  if (accountType) {
    Cookies.set("account_type", accountType, opts);
  }
  if (botType) {
    Cookies.set("bot_type", botType, opts);
  } else {
    removeCookie("bot_type");
  }
}

export function syncAccountTypeCookie(accountType, botType) {
  const opts = getAuthCookieOptions();
  const normalized = (accountType || "client").trim().toLowerCase();
  Cookies.set("account_type", normalized, opts);
  if (botType) {
    Cookies.set("bot_type", botType, opts);
  }
}

export function clearAuthSessionCookies() {
  ["token", "email", "bot_type", "account_type", "impersonation_token", "impersonation_bot_type"].forEach(
    removeCookie
  );
  if (typeof window !== "undefined") {
    try {
      sessionStorage.removeItem("impersonation_client_name");
    } catch {
      /* ignore */
    }
  }
}

const IMPERSONATION_COOKIE_DAYS = 1 / 24; // ~1 hour; JWT exp is authoritative

export function setImpersonationCookies({ token, botType, clientName }) {
  const opts = { ...getAuthCookieOptions(), expires: IMPERSONATION_COOKIE_DAYS };
  if (token) {
    Cookies.set("impersonation_token", token, opts);
  }
  if (botType) {
    Cookies.set("impersonation_bot_type", botType, opts);
  }
  if (typeof window !== "undefined" && clientName) {
    try {
      sessionStorage.setItem("impersonation_client_name", clientName);
    } catch {
      /* ignore */
    }
  }
}

export function clearImpersonationCookies() {
  removeCookie("impersonation_token");
  removeCookie("impersonation_bot_type");
  if (typeof window !== "undefined") {
    try {
      sessionStorage.removeItem("impersonation_client_name");
    } catch {
      /* ignore */
    }
  }
}

const ADMIN_RETURN_KEY = "admin_return_session";

export function saveAdminReturnSession({ token, adminEmail, targetEmail, targetName }) {
  if (typeof window === "undefined") return;
  try {
    sessionStorage.setItem(
      ADMIN_RETURN_KEY,
      JSON.stringify({ token, adminEmail, targetEmail, targetName })
    );
  } catch {
    /* ignore */
  }
}

export function getAdminReturnSession() {
  if (typeof window === "undefined") return null;
  try {
    const raw = sessionStorage.getItem(ADMIN_RETURN_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

export function clearAdminReturnSession() {
  if (typeof window === "undefined") return;
  try {
    sessionStorage.removeItem(ADMIN_RETURN_KEY);
  } catch {
    /* ignore */
  }
}

export function hasImpersonationSession() {
  return !!Cookies.get("impersonation_token");
}

export function getImpersonationMeta() {
  if (typeof window === "undefined") {
    return { clientName: null, botType: Cookies.get("impersonation_bot_type") || null };
  }
  let clientName = null;
  try {
    clientName = sessionStorage.getItem("impersonation_client_name");
  } catch {
    clientName = null;
  }
  return {
    clientName,
    botType: Cookies.get("impersonation_bot_type") || null,
  };
}

export function getEffectiveBotType() {
  return Cookies.get("impersonation_bot_type") || Cookies.get("bot_type") || "real_estate";
}

export function normalizeAccountType(accountType) {
  return (accountType || "client").trim().toLowerCase();
}

export function isAbsoluteUrl(url) {
  return typeof url === "string" && /^https?:\/\//i.test(url);
}

export function normalizeAppRedirectUrl(url) {
  if (!url || typeof url !== "string" || !isAbsoluteUrl(url)) {
    return url;
  }

  const appOrigin = getAppOrigin();
  if (!appOrigin) {
    return url;
  }

  try {
    const target = new URL(url);
    const appHost = new URL(appOrigin).hostname.toLowerCase();
    if (target.hostname.toLowerCase() !== appHost) {
      return `${appOrigin.replace(/\/$/, "")}${target.pathname}${target.search}${target.hash}`;
    }
  } catch {
    return url;
  }

  return url;
}

export function resolvePostAuthRedirect({
  accountType,
  defaultRoute,
  fallbackFrom,
  returnTo,
}) {
  const type = normalizeAccountType(accountType);

  if (returnTo && returnTo.startsWith("/")) {
    if (type === "admin" && !returnTo.startsWith("/admin")) {
      return "/admin";
    }
    if (type === "partner" && returnTo.startsWith("/dashboard")) {
      return "/partner/dashboard";
    }
    if (type === "client" && returnTo.startsWith("/partner")) {
      return "/dashboard";
    }
    if (type === "client" && returnTo.startsWith("/admin")) {
      return "/dashboard";
    }
    return returnTo;
  }

  let route;
  if (type === "admin") {
    route = "/admin";
  } else if (type === "partner") {
    route = defaultRoute || "/partner/dashboard";
  } else {
    route = fallbackFrom || defaultRoute || "/dashboard";
  }

  return normalizeAppRedirectUrl(route);
}

export function applyPostAuthRedirect(navigate, redirectTo) {
  const normalized = normalizeAppRedirectUrl(redirectTo);

  if (isAbsoluteUrl(normalized)) {
    try {
      const parsed = new URL(normalized);
      navigate(`${parsed.pathname}${parsed.search}${parsed.hash}`, { replace: true });
      return;
    } catch {
      window.location.assign(normalized);
      return;
    }
  }

  navigate(normalized, { replace: true });
}
